import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Search, Loader2, FileDown, ShieldX, RefreshCw, ExternalLink, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { GoldButton, GhostButton } from "../../components/UI";

const FILTERS = [
  { key: "ALL", label: "Όλα / All" },
  { key: "VALID", label: "Valid" },
  { key: "REVOKED", label: "Revoked" },
];
const rank = (v) => (v ? `#${Number(v).toLocaleString("en-US")}` : "—");
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

function Modal({ testid, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={testid} onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.30)] p-6" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function RevokeModal({ cert, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/certificates/${cert.certificate_no}/revoke`, { reason: reason.trim() || null });
      toast.success(`${cert.certificate_no} revoked`);
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Revoke failed"));
      setBusy(false);
    }
  };
  return (
    <Modal testid="cert-revoke-modal" onClose={onClose}>
      <h3 lang="el" className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2"><ShieldX className="w-5 h-5 text-rose-400" /> Ανάκληση πιστοποιητικού</h3>
      <p className="text-sm text-slate-300 mt-2 font-mono">{cert.certificate_no} · {cert.member_name}</p>
      <p className="text-sm text-slate-400 mt-2">Η δημόσια σελίδα επαλήθευσης θα δείχνει αμέσως <span className="text-rose-300 font-bold">REVOKED</span>. Η ενέργεια δεν αναιρείται και καταγράφεται στο audit log.</p>
      <label lang="el" className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-4">Αιτιολογία (προαιρετικά · εμφανίζεται δημόσια)</label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} data-testid="cert-revoke-reason"
        className="w-full mt-1 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60"
        placeholder="π.χ. Issued with incorrect details" />
      <div className="flex justify-end gap-2 mt-4">
        <GhostButton onClick={onClose} data-testid="cert-revoke-cancel">Άκυρο</GhostButton>
        <button onClick={submit} disabled={busy} data-testid="cert-revoke-confirm"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40" style={{ transition: "background-color 0.15s ease" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldX className="w-4 h-4" />} Ανάκληση
        </button>
      </div>
    </Modal>
  );
}

function ReissueModal({ cert, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${cert.member_id}/certificates`);
      setResult(data.certificate);
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Re-issue failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal testid="cert-reissue-modal" onClose={onClose}>
      <h3 lang="el" className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-[#E5B842]" /> Επανέκδοση</h3>
      <p className="text-sm text-slate-300 mt-2">{cert.member_name} <span className="font-mono text-slate-500">· {cert.member_public_id}</span></p>
      {result ? (
        <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="cert-reissue-result">
          <div className="flex items-center gap-2 text-emerald-300 font-bold text-sm"><CheckCircle2 className="w-4 h-4" /> Νέο πιστοποιητικό εκδόθηκε</div>
          <div className="font-mono text-2xl text-[#E5B842] mt-2" data-testid="cert-reissue-new-number">{result.certificate_no}</div>
          <div className="text-xs font-mono text-slate-400 mt-1">World {rank(result.world_rank)} · {result.country_name} {rank(result.country_rank)}</div>
          <a href={`/verify/${result.certificate_no}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-3 text-slate-300 hover:text-[#E5B842]" data-testid="cert-reissue-verify-link"><ExternalLink className="w-3 h-3" /> /verify/{result.certificate_no}</a>
          <div className="flex justify-end mt-3"><GhostButton onClick={onClose} data-testid="cert-reissue-close">Κλείσιμο</GhostButton></div>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-400 mt-2">Δημιουργείται <b className="text-slate-200">νέος αριθμός πιστοποιητικού</b> με τις <b className="text-slate-200">τρέχουσες</b> κατατάξεις. Το {cert.certificate_no} παραμένει ως έχει (ανάκλησέ το ξεχωριστά αν χρειάζεται). Δεν μετράει στο ημερήσιο όριο του μέλους.</p>
          <div className="flex justify-end gap-2 mt-5">
            <GhostButton onClick={onClose} data-testid="cert-reissue-cancel">Άκυρο</GhostButton>
            <GoldButton onClick={submit} disabled={busy} data-testid="cert-reissue-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Επανέκδοση
            </GoldButton>
          </div>
        </>
      )}
    </Modal>
  );
}

function CertRow({ c, onView, onRevoke, onReissue, busy }) {
  const valid = c.status === "VALID";
  return (
    <tr className="border-t border-[rgba(212,175,55,0.10)] hover:bg-[#121212]" data-testid={`cert-row-${c.certificate_no}`} style={{ transition: "background-color 0.15s ease" }}>
      <td className="py-3 pr-4 font-mono text-sm text-[#E5B842] whitespace-nowrap">{c.certificate_no}</td>
      <td className="py-3 pr-4">
        <div className="text-sm text-slate-100">{c.member_name}</div>
        <div className="text-[11px] font-mono text-slate-500">{c.member_public_id}{c.admin_issued ? " · admin-issued" : ""}</div>
      </td>
      <td className="py-3 pr-4 text-sm text-slate-300">{c.country_name || "—"}</td>
      <td className="py-3 pr-4 font-mono text-xs text-slate-300 whitespace-nowrap">W {rank(c.world_rank)} · C {rank(c.country_rank)}</td>
      <td className="py-3 pr-4 font-mono text-xs text-slate-400 whitespace-nowrap">{fmtDate(c.issued_at)}</td>
      <td className="py-3 pr-4">
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${valid ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"}`}
          title={c.revoke_reason || ""} data-testid={`cert-status-${c.certificate_no}`}>{c.status}</span>
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1.5 justify-end">
          <GhostButton onClick={() => onView(c)} disabled={busy} data-testid={`cert-view-${c.certificate_no}`}><FileDown className="w-4 h-4" /> Προβολή</GhostButton>
          <a href={`/verify/${c.certificate_no}`} target="_blank" rel="noreferrer" data-testid={`cert-verify-${c.certificate_no}`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs text-slate-300 border border-[rgba(212,175,55,0.22)] hover:text-[#E5B842]"><ExternalLink className="w-3.5 h-3.5" /> Verify</a>
          {valid && <GhostButton onClick={() => onRevoke(c)} data-testid={`cert-revoke-${c.certificate_no}`}><ShieldX className="w-4 h-4" /> Ανάκληση</GhostButton>}
          <GhostButton onClick={() => onReissue(c)} disabled={!c.member_id} data-testid={`cert-reissue-${c.certificate_no}`}><RefreshCw className="w-4 h-4" /> Επανέκδοση</GhostButton>
        </div>
      </td>
    </tr>
  );
}

export default function AdminCertificates() {
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [reissuing, setReissuing] = useState(null);
  const [busyNo, setBusyNo] = useState(null);
  const seq = useRef(0);

  useEffect(() => { const t = setTimeout(() => setSearch(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [status, search]);

  const load = useCallback(async () => {
    const s = ++seq.current;
    try {
      const { data: d } = await api.get("/admin/certificates", { params: { status, search: search || undefined, page } });
      if (s === seq.current) setData(d);
    } catch (e) {
      if (s === seq.current) { toast.error(apiErr(e, "Failed to load certificates")); setData({ certificates: [], total: 0, page_size: 50 }); }
    }
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);

  const view = async (c) => {
    setBusyNo(c.certificate_no);
    try {
      const res = await api.get(`/certificates/${c.certificate_no}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiErr(e, "Download failed"));
    } finally {
      setBusyNo(null);
    }
  };

  const rows = data?.certificates;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / (data?.page_size || 50)));
  return (
    <AdminLayout>
      <div data-testid="admin-certificates-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold">Certificate of Membership</div>
        <h1 lang="el" className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 mt-1 flex items-center gap-3"><Award className="w-8 h-8 text-[#E5B842]" /> Πιστοποιητικά</h1>
        <p className="text-sm text-slate-400 mt-2" data-testid="admin-certificates-total">{data ? `${data.total} certificates` : "…"}</p>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αριθμός, όνομα μέλους ή WSTF ID" data-testid="certs-search"
              className="w-full bg-[#0f0f0f] border border-[rgba(212,175,55,0.22)] rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
          </div>
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setStatus(f.key)} data-testid={`certs-filter-${f.key.toLowerCase()}`} aria-pressed={status === f.key}
                lang="el" className={`px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${status === f.key ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{f.label}</button>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.18)] px-5 overflow-x-auto" data-testid="certs-table">
          {!rows ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#E5B842]" /></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="certs-empty">Δεν βρέθηκαν πιστοποιητικά.</div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr lang="el" className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                  <th className="py-3 pr-4 font-normal">Αριθμός</th><th className="py-3 pr-4 font-normal">Μέλος</th><th className="py-3 pr-4 font-normal">Χώρα</th>
                  <th className="py-3 pr-4 font-normal">Ranks (snapshot)</th><th className="py-3 pr-4 font-normal">Έκδοση</th><th className="py-3 pr-4 font-normal">Status</th><th className="py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => <CertRow key={c.certificate_no} c={c} busy={busyNo === c.certificate_no} onView={view} onRevoke={setRevoking} onReissue={setReissuing} />)}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500" data-testid="certs-page-indicator">Page {page} / {totalPages}</div>
          <div className="flex gap-2">
            <GhostButton data-testid="certs-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="w-4 h-4" /> Prev</GhostButton>
            <GhostButton data-testid="certs-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next <ChevronRight className="w-4 h-4" /></GhostButton>
          </div>
        </div>
      </div>
      {revoking && <RevokeModal cert={revoking} onClose={() => setRevoking(null)} onDone={() => { setRevoking(null); load(); }} />}
      {reissuing && <ReissueModal cert={reissuing} onClose={() => setReissuing(null)} onDone={load} />}
    </AdminLayout>
  );
}
